// ══════════════════════════════════════════════════════════════════════════════
// MODAL DE CRÉATION DE PUBLICATION - Composant Réutilisable
// Types: Article Blog, Photo, Vidéo, Réel, Avant/Après
// Utilisé par: MesPublications, CockpitFAB, PublishFAB
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, X, Image, Video, Sparkles,
  Camera, Send, Globe, Users, Lock, Loader2, ArrowLeft,
  MapPin, ChevronRight, RefreshCw, Smartphone, Upload, Hash,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ══════════════════════════════════════════════════════════════════════════════
// LISTE DES MÉTIERS
// ══════════════════════════════════════════════════════════════════════════════
const METIERS_LIST = [
  { value: 'pisciniste', label: 'Pisciniste', color: '#06B6D4' },
  { value: 'plombier', label: 'Plombier', color: '#3B82F6' },
  { value: 'electricien', label: 'Électricien', color: '#EAB308' },
  { value: 'macon', label: 'Maçon', color: '#EA580C' },
  { value: 'carreleur', label: 'Carreleur', color: '#10B981' },
  { value: 'peintre', label: 'Peintre', color: '#EC4899' },
  { value: 'menuisier', label: 'Menuisier', color: '#D97706' },
  { value: 'couvreur', label: 'Couvreur', color: '#8B5CF6' },
  { value: 'chauffagiste', label: 'Chauffagiste', color: '#EF4444' },
  { value: 'paysagiste', label: 'Paysagiste', color: '#22C55E' },
  { value: 'climaticien', label: 'Climaticien', color: '#0891B2' },
  { value: 'jardinier', label: 'Jardinier', color: '#16A34A' },
  { value: 'facadier', label: 'Façadier', color: '#A855F7' },
  { value: 'serrurier', label: 'Serrurier', color: '#64748B' },
  { value: 'charpentier', label: 'Charpentier', color: '#92400E' },
  { value: 'plaquiste', label: 'Plaquiste', color: '#6366F1' },
  { value: 'cuisiniste', label: 'Cuisiniste', color: '#F97316' },
  { value: 'architecte', label: 'Architecte', color: '#1E3A5F' },
];

// ══════════════════════════════════════════════════════════════════════════════
// TYPES DE PUBLICATIONS
// ══════════════════════════════════════════════════════════════════════════════
const POST_TYPES = [
  { id: 'article_blog', label: 'Article Blog', icon: FileText, description: 'Article complet pour ton blog', color: 'blue' },
  { id: 'photo', label: 'Photo', icon: Image, description: 'Image avec légende', color: 'green' },
  { id: 'video', label: 'Vidéo', icon: Video, description: 'Vidéo horizontale (16:9), max 3 min', color: 'purple', ratio: '16:9', maxDuration: 180 },
  { id: 'reel', label: 'Réel', icon: Smartphone, description: 'Vidéo verticale (9:16), max 60 sec', color: 'pink', ratio: '9:16', maxDuration: 60 },
  { id: 'avant_apres', label: 'Avant/Après', icon: RefreshCw, description: 'Transformation avec slider', color: 'orange' },
];

// ══════════════════════════════════════════════════════════════════════════════
// COMPOSANT MODAL
// ══════════════════════════════════════════════════════════════════════════════
export default function CreatePublicationModal({ isOpen, onClose, onSuccess, token, onAskAbra }) {
  const [step, setStep] = useState(1); // 1 = choix type, 2 = formulaire
  const [postType, setPostType] = useState(null);
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [beforeFile, setBeforeFile] = useState(null);
  const [beforePreview, setBeforePreview] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [afterPreview, setAfterPreview] = useState(null);
  const [visibility, setVisibility] = useState('public');
  const [loading, setLoading] = useState(false);
  const [selectedMetier, setSelectedMetier] = useState('');
  const [location, setLocation] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [locationCoords, setLocationCoords] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  
  // États pour Article Blog
  const [excerpt, setExcerpt] = useState('');
  const [tags, setTags] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [showSEO, setShowSEO] = useState(false);
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  
  const fileInputRef = useRef(null);
  const beforeInputRef = useRef(null);
  const afterInputRef = useRef(null);
  const locationInputRef = useRef(null);
  const coverInputRef = useRef(null);
  
  // Gestion du clavier mobile
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [isInputFocused, setIsInputFocused] = useState(false);
  
  useEffect(() => {
    if (!isOpen) return;
    
    const handleResize = () => {
      if (window.visualViewport) {
        const heightDiff = window.innerHeight - window.visualViewport.height;
        setKeyboardHeight(heightDiff > 100 ? heightDiff : 0);
      }
    };
    
    window.visualViewport?.addEventListener('resize', handleResize);
    document.body.classList.add('modal-fullscreen-open');
    
    return () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
      document.body.classList.remove('modal-fullscreen-open');
    };
  }, [isOpen]);

  // Géolocalisation automatique
  useEffect(() => {
    if (isOpen && !location && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          try {
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&format=json`
            );
            const data = await res.json();
            const city = data.address?.city || data.address?.town || data.address?.village || '';
            const postalCode = data.address?.postcode || '';
            if (city) {
              const locationStr = `${city} (${postalCode})`;
              setLocation(locationStr);
              setLocationInput(locationStr);
              setLocationCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude });
            }
          } catch (e) {
            console.log('Geocoding failed');
          }
        },
        () => {}
      );
    }
  }, [isOpen, location]);

  // Recherche d'adresse avec l'API adresse.data.gouv.fr
  const searchLocation = async (query) => {
    if (!query || query.length < 2) {
      setLocationSuggestions([]);
      return;
    }
    
    try {
      // API française pour les codes postaux et villes
      const res = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&type=municipality&limit=5`
      );
      const data = await res.json();
      
      if (data.features && data.features.length > 0) {
        const suggestions = data.features.map(f => ({
          label: `${f.properties.city || f.properties.name} (${f.properties.postcode})`,
          city: f.properties.city || f.properties.name,
          postcode: f.properties.postcode,
          coords: {
            lat: f.geometry.coordinates[1],
            lon: f.geometry.coordinates[0]
          }
        }));
        setLocationSuggestions(suggestions);
      } else {
        setLocationSuggestions([]);
      }
    } catch (e) {
      console.log('Location search failed:', e);
      setLocationSuggestions([]);
    }
  };

  // Debounce pour la recherche
  useEffect(() => {
    const timer = setTimeout(() => {
      if (locationInput && locationInput !== location) {
        searchLocation(locationInput);
        setShowLocationSuggestions(true);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [locationInput, location]);

  const handleSelectLocation = (suggestion) => {
    setLocation(suggestion.label);
    setLocationInput(suggestion.label);
    setLocationCoords(suggestion.coords);
    setShowLocationSuggestions(false);
    setLocationSuggestions([]);
  };

  const resetForm = () => {
    setStep(1);
    setPostType(null);
    setContent('');
    setTitle('');
    setMediaFile(null);
    setMediaPreview(null);
    setBeforeFile(null);
    setBeforePreview(null);
    setAfterFile(null);
    setAfterPreview(null);
    setVisibility('public');
    setSelectedMetier('');
    setVideoDuration(0);
    setLocation('');
    setLocationInput('');
    setLocationSuggestions([]);
    setShowLocationSuggestions(false);
    setLocationCoords(null);
    // Article Blog
    setExcerpt('');
    setTags('');
    setCoverImage(null);
    setShowSEO(false);
    setSeoTitle('');
    setSeoDescription('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSelectType = (type) => {
    setPostType(type);
    setStep(2);
  };

  // Upload image de couverture (Article Blog)
  const handleCoverUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Sélectionne un fichier image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop lourde (max 5 Mo)');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setCoverImage(ev.target?.result);
    reader.readAsDataURL(file);
  };

  const handleFileSelect = (e, type = 'main') => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const isVideo = postType === 'video' || postType === 'reel';
    const maxSize = isVideo ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
    
    if (file.size > maxSize) {
      toast.error(`Fichier trop volumineux (max ${isVideo ? '100' : '10'}MB)`);
      return;
    }
    
    if (isVideo) {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        const duration = video.duration;
        const maxDuration = postType === 'reel' ? 60 : 180;
        if (duration > maxDuration) {
          toast.error(`Vidéo trop longue (max ${maxDuration} secondes)`);
          return;
        }
        setVideoDuration(Math.round(duration));
        processFile(file, type);
      };
      video.src = URL.createObjectURL(file);
    } else {
      processFile(file, type);
    }
  };

  const processFile = (file, type) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (type === 'before') {
        setBeforeFile(file);
        setBeforePreview(e.target?.result);
      } else if (type === 'after') {
        setAfterFile(file);
        setAfterPreview(e.target?.result);
      } else {
        setMediaFile(file);
        setMediaPreview(e.target?.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (asDraft = false) => {
    // Validation du métier - OBLIGATOIRE
    if (!selectedMetier) {
      toast.error('Choisis un métier pour ta publication');
      return;
    }
    
    // Validation du contenu
    if (postType === 'avant_apres') {
      if (!beforeFile || !afterFile) {
        toast.error('Ajoute les photos Avant et Après');
        return;
      }
    } else if (!content.trim() && !mediaFile && postType !== 'photo') {
      toast.error('Ajoute du contenu');
      return;
    } else if ((postType === 'photo' || postType === 'video' || postType === 'reel') && !mediaFile) {
      toast.error(`Ajoute ${postType === 'photo' ? 'une photo' : 'une vidéo'}`);
      return;
    }
    
    setLoading(true);
    
    try {
      // Upload des médias
      let mediaUrl = null;
      let beforeUrl = null;
      let afterUrl = null;
      
      const uploadFile = async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const uploadRes = await fetch(`${API_URL}/api/upload/media`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        if (uploadRes.ok) {
          const data = await uploadRes.json();
          return data.url;
        }
        return null;
      };
      
      if (postType === 'avant_apres') {
        beforeUrl = await uploadFile(beforeFile);
        afterUrl = await uploadFile(afterFile);
      } else if (postType === 'article_blog' && coverImage) {
        // Pour article blog, uploader l'image de couverture
        const blob = await fetch(coverImage).then(r => r.blob());
        const file = new File([blob], 'cover.jpg', { type: blob.type });
        mediaUrl = await uploadFile(file);
      } else if (mediaFile) {
        mediaUrl = await uploadFile(mediaFile);
      }
      
      // Créer la publication
      const postData = {
        content: content.trim(),
        title: title.trim() || null,
        post_type: postType,
        media_url: mediaUrl,
        before_media_url: beforeUrl,
        after_media_url: afterUrl,
        visibility: visibility,
        status: asDraft ? 'draft' : 'published',
        location: location,
        location_coords: locationCoords,
        metier: selectedMetier || null,
        video_ratio: postType === 'video' ? '16:9' : (postType === 'reel' ? '9:16' : null),
        video_duration: videoDuration > 0 ? videoDuration : null,
        // Article Blog - champs additionnels
        excerpt: postType === 'article_blog' ? excerpt.trim() : null,
        tags: postType === 'article_blog' && tags ? tags.split(',').map(t => t.trim()).filter(Boolean) : null,
        seo_title: postType === 'article_blog' ? (seoTitle.trim() || title.trim()) : null,
        seo_description: postType === 'article_blog' ? (seoDescription.trim() || excerpt.trim()) : null,
        cover_image_url: postType === 'article_blog' ? mediaUrl : null,
      };
      
      const res = await fetch(`${API_URL}/api/ecosystem/posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(postData)
      });
      
      if (res.ok) {
        toast.success(asDraft ? 'Brouillon enregistré !' : 'Publication créée !');
        handleClose();
        onSuccess?.();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erreur lors de la publication');
      }
    } catch (e) {
      console.error('Error creating post:', e);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const currentTypeConfig = POST_TYPES.find(t => t.id === postType);
  
  const visibilityOptions = [
    { id: 'public', label: 'Public', icon: Globe },
    { id: 'followers', label: 'Abonnés', icon: Users },
    { id: 'private', label: 'Privé', icon: Lock },
  ];

  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    pink: 'bg-pink-50 text-pink-600 border-pink-200',
    orange: 'bg-orange-50 text-orange-600 border-orange-200',
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] bg-black/50 flex items-end sm:items-center justify-center"
        onClick={handleClose}
      >
        <motion.div
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full sm:max-w-lg bg-white sm:rounded-2xl overflow-hidden flex flex-col"
          style={{ 
            height: '100dvh',
            maxHeight: '100dvh',
            paddingBottom: keyboardHeight > 0 ? keyboardHeight : 0
          }}
        >
          {/* Header - Étape 1 : Titre avec + et croix */}
          {step === 1 && (
            <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200 bg-white flex-shrink-0">
              {/* Titre avec icône + */}
              <div className="flex items-center gap-3 flex-1">
                <div className="w-10 h-10 rounded-xl bg-[#1E3A5F] flex items-center justify-center border-2 border-orange-500">
                  <span className="text-orange-500 text-xl font-bold leading-none">+</span>
                </div>
                <h1 className="text-lg font-bold text-[#1E3A5F]">Que veux-tu publier ?</h1>
              </div>
              {/* Bouton fermer */}
              <button
                onClick={handleClose}
                className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                data-testid="close-publication-modal"
              >
                <X size={22} className="text-gray-600" />
              </button>
            </div>
          )}
          
          {/* Header - Étape 2 : Retour et type sélectionné */}
          {step === 2 && (
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0">
              <button
                onClick={() => setStep(1)}
                className="p-2 -ml-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <ArrowLeft size={22} className="text-gray-600" />
              </button>
              <h2 className="text-base font-bold text-[#1E3A5F]">
                {currentTypeConfig?.label}
              </h2>
              <div className="w-10" />
            </div>
          )}

          {/* Contenu */}
          <div className="flex-1 overflow-y-auto">
            {step === 1 ? (
              /* Étape 1 : Choix du type */
              <div className="p-4">
                
                <div className="space-y-3">
                  {POST_TYPES.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.id}
                        onClick={() => handleSelectType(type.id)}
                        className="w-full flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:border-orange-300 hover:bg-orange-50/50 transition-all text-left"
                        data-testid={`select-type-${type.id}`}
                      >
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[type.color]}`}>
                          <Icon size={24} />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">{type.label}</p>
                          <p className="text-sm text-gray-500">{type.description}</p>
                        </div>
                        <ChevronRight size={20} className="text-gray-400" />
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* Étape 2 : Formulaire de création */
              <div className="p-4 space-y-4">
                {/* ═══════════════════════════════════════════════════════════
                    FORMULAIRE ARTICLE BLOG
                ═══════════════════════════════════════════════════════════ */}
                {postType === 'article_blog' && (
                  <>
                    {/* Image de couverture */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Image de couverture</label>
                      {coverImage ? (
                        <div className="relative aspect-[2/1] rounded-xl overflow-hidden border border-gray-200">
                          <img src={coverImage} alt="Couverture" className="w-full h-full object-cover" />
                          <button 
                            onClick={() => setCoverImage(null)}
                            className="absolute top-2 right-2 p-2 bg-white/90 rounded-full hover:bg-white shadow-md"
                          >
                            <X size={16} className="text-gray-700" />
                          </button>
                        </div>
                      ) : (
                        <div 
                          onClick={() => coverInputRef.current?.click()}
                          className="aspect-[2/1] border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-orange-400 hover:bg-orange-50/50 transition-colors"
                        >
                          <Upload size={28} className="text-gray-400 mb-2" />
                          <p className="text-sm text-gray-600">Ajouter une image de couverture</p>
                          <p className="text-xs text-gray-400 mt-1">Recommandé : 1200 × 600 px</p>
                        </div>
                      )}
                      <input ref={coverInputRef} type="file" accept="image/*" onChange={handleCoverUpload} className="hidden" />
                    </div>

                    {/* Titre de l'article */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Titre de l'article *</label>
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        onFocus={(e) => {
                          setIsInputFocused(true);
                          setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
                        }}
                        onBlur={() => setIsInputFocused(false)}
                        placeholder="Un titre accrocheur pour ton article..."
                        className="w-full px-4 py-3 text-base font-semibold border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>

                    {/* Résumé / Accroche */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Résumé / Accroche</label>
                      <textarea
                        value={excerpt}
                        onChange={(e) => setExcerpt(e.target.value)}
                        onFocus={(e) => {
                          setIsInputFocused(true);
                          setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
                        }}
                        onBlur={() => setIsInputFocused(false)}
                        placeholder="Un court résumé qui donne envie de lire..."
                        rows={2}
                        maxLength={200}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                      />
                      <p className="text-xs text-gray-400 text-right mt-1">{excerpt.length}/200</p>
                    </div>

                    {/* Métier concerné - Filtres horizontaux */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Catégorie (Métier) <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div 
                          className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1 pr-6"
                          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                        >
                          {METIERS_LIST.map((metier) => (
                            <button
                              key={metier.value}
                              onClick={() => setSelectedMetier(metier.value)}
                              className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                                selectedMetier === metier.value
                                  ? 'bg-orange-500 text-white shadow-sm'
                                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                              }`}
                            >
                              {metier.label}
                            </button>
                          ))}
                        </div>
                        <div className="absolute right-0 top-0 bottom-1 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none flex items-center justify-end pr-1">
                          <ChevronRight size={14} className="text-orange-500 animate-pulse" />
                        </div>
                      </div>
                      {!selectedMetier && (
                        <p className="text-xs text-red-500 mt-1">Choisis un métier pour ta publication</p>
                      )}
                    </div>

                    {/* Contenu de l'article */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Contenu de l'article *</label>
                      <textarea
                        value={content}
                        onChange={(e) => {
                          if (e.target.value.length <= 15000) {
                            setContent(e.target.value);
                          }
                        }}
                        onFocus={(e) => {
                          setIsInputFocused(true);
                          setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
                        }}
                        onBlur={() => setIsInputFocused(false)}
                        placeholder="Écris ton article ici..."
                        rows={8}
                        maxLength={15000}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                      />
                      <p className="text-xs text-gray-400 text-right mt-1">
                        {content.length.toLocaleString('fr-FR')}/15 000 caractères • {content.split(' ').filter(w => w).length} mots • ~{Math.ceil(content.split(' ').length / 200) || 1} min de lecture
                      </p>
                    </div>

                    {/* Tags */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Hash size={14} className="inline mr-1" />
                        Tags (séparés par des virgules)
                      </label>
                      <input
                        type="text"
                        value={tags}
                        onChange={(e) => setTags(e.target.value)}
                        onFocus={(e) => {
                          setIsInputFocused(true);
                          setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
                        }}
                        onBlur={() => setIsInputFocused(false)}
                        placeholder="rénovation, tutoriel, conseils..."
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>

                    {/* Référencement SEO - Accordéon */}
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <button
                        onClick={() => setShowSEO(!showSEO)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <Globe size={16} className="text-gray-500" />
                          <span className="font-medium text-gray-700 text-sm">Référencement SEO</span>
                        </div>
                        <ChevronDown size={16} className={`text-gray-400 transition-transform ${showSEO ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {showSEO && (
                        <div className="p-4 space-y-3 border-t border-gray-200">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Titre SEO</label>
                            <input
                              type="text"
                              value={seoTitle}
                              onChange={(e) => setSeoTitle(e.target.value)}
                              onFocus={(e) => {
                                setIsInputFocused(true);
                                setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
                              }}
                              onBlur={() => setIsInputFocused(false)}
                              placeholder={title || 'Titre pour les moteurs de recherche'}
                              maxLength={60}
                              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                            />
                            <p className="text-xs text-gray-400 text-right mt-0.5">{seoTitle.length}/60</p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Description SEO</label>
                            <textarea
                              value={seoDescription}
                              onChange={(e) => setSeoDescription(e.target.value)}
                              onFocus={(e) => {
                                setIsInputFocused(true);
                                setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
                              }}
                              onBlur={() => setIsInputFocused(false)}
                              placeholder={excerpt || 'Description pour les moteurs de recherche'}
                              maxLength={160}
                              rows={2}
                              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                            />
                            <p className="text-xs text-gray-400 text-right mt-0.5">{seoDescription.length}/160</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Visibilité */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Visibilité</label>
                      <div className="flex gap-2">
                        {[
                          { id: 'public', label: 'Public', icon: Globe },
                          { id: 'followers', label: 'Abonnés', icon: Users },
                          { id: 'private', label: 'Privé', icon: Lock },
                        ].map((opt) => {
                          const Icon = opt.icon;
                          return (
                            <button
                              key={opt.id}
                              onClick={() => setVisibility(opt.id)}
                              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-sm font-medium transition-all ${
                                visibility === opt.id
                                  ? 'bg-orange-500 text-white'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              <Icon size={16} />
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}

                {/* ═══════════════════════════════════════════════════════════
                    FORMULAIRES PHOTO, VIDEO, REEL, AVANT/APRES (inchangés)
                ═══════════════════════════════════════════════════════════ */}

                {/* Zone de média - Photo */}
                {postType === 'photo' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Photo</label>
                    {mediaPreview ? (
                      <div className="relative rounded-xl overflow-hidden bg-gray-100">
                        <img src={mediaPreview} alt="Aperçu" className="w-full max-h-64 object-cover" />
                        <button
                          onClick={() => { setMediaFile(null); setMediaPreview(null); }}
                          className="absolute top-2 right-2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full h-40 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-2 hover:border-orange-400 hover:bg-orange-50/50 transition-all"
                      >
                        <Camera size={32} className="text-gray-400" />
                        <p className="text-sm text-gray-500">Clique pour ajouter une photo</p>
                        <p className="text-xs text-gray-400">Max 10MB</p>
                      </button>
                    )}
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => handleFileSelect(e)} className="hidden" />
                  </div>
                )}

                {/* Zone de média - Vidéo */}
                {(postType === 'video' || postType === 'reel') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {postType === 'reel' ? 'Réel (vertical 9:16)' : 'Vidéo (horizontal 16:9)'}
                    </label>
                    {mediaPreview ? (
                      <div className="relative rounded-xl overflow-hidden bg-gray-100">
                        <video src={mediaPreview} controls className={`w-full ${postType === 'reel' ? 'max-h-80' : 'max-h-48'}`} />
                        <button
                          onClick={() => { setMediaFile(null); setMediaPreview(null); setVideoDuration(0); }}
                          className="absolute top-2 right-2 w-8 h-8 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70"
                        >
                          <X size={18} />
                        </button>
                        {videoDuration > 0 && (
                          <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 rounded text-white text-xs">
                            {Math.floor(videoDuration / 60)}:{String(videoDuration % 60).padStart(2, '0')}
                          </div>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className={`w-full border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-2 hover:border-orange-400 hover:bg-orange-50/50 transition-all ${postType === 'reel' ? 'h-56' : 'h-40'}`}
                      >
                        <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${postType === 'reel' ? 'bg-pink-50' : 'bg-purple-50'}`}>
                          {postType === 'reel' ? <Smartphone size={28} className="text-pink-500" /> : <Video size={28} className="text-purple-500" />}
                        </div>
                        <p className="text-sm text-gray-500">
                          {postType === 'reel' ? 'Vidéo verticale (9:16)' : 'Vidéo horizontale (16:9)'}
                        </p>
                        <p className="text-xs text-gray-400">
                          Max {postType === 'reel' ? '60 sec' : '3 min'} • 100MB
                        </p>
                      </button>
                    )}
                    <input ref={fileInputRef} type="file" accept="video/*" onChange={(e) => handleFileSelect(e)} className="hidden" />
                  </div>
                )}

                {/* Zone Avant/Après */}
                {postType === 'avant_apres' && (
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">Photos Avant/Après</label>
                    <div className="grid grid-cols-2 gap-3">
                      {/* Photo Avant */}
                      <div>
                        <p className="text-xs text-gray-500 mb-1 text-center">AVANT</p>
                        {beforePreview ? (
                          <div className="relative rounded-xl overflow-hidden bg-gray-100 aspect-square">
                            <img src={beforePreview} alt="Avant" className="w-full h-full object-cover" />
                            <button
                              onClick={() => { setBeforeFile(null); setBeforePreview(null); }}
                              className="absolute top-1 right-1 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center text-white"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => beforeInputRef.current?.click()}
                            className="w-full aspect-square border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-1 hover:border-orange-400 hover:bg-orange-50/50 transition-all"
                          >
                            <Camera size={24} className="text-gray-400" />
                            <p className="text-xs text-gray-400">Photo avant</p>
                          </button>
                        )}
                        <input ref={beforeInputRef} type="file" accept="image/*" onChange={(e) => handleFileSelect(e, 'before')} className="hidden" />
                      </div>
                      
                      {/* Photo Après */}
                      <div>
                        <p className="text-xs text-gray-500 mb-1 text-center">APRÈS</p>
                        {afterPreview ? (
                          <div className="relative rounded-xl overflow-hidden bg-gray-100 aspect-square">
                            <img src={afterPreview} alt="Après" className="w-full h-full object-cover" />
                            <button
                              onClick={() => { setAfterFile(null); setAfterPreview(null); }}
                              className="absolute top-1 right-1 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center text-white"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => afterInputRef.current?.click()}
                            className="w-full aspect-square border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-1 hover:border-orange-400 hover:bg-orange-50/50 transition-all"
                          >
                            <Camera size={24} className="text-gray-400" />
                            <p className="text-xs text-gray-400">Photo après</p>
                          </button>
                        )}
                        <input ref={afterInputRef} type="file" accept="image/*" onChange={(e) => handleFileSelect(e, 'after')} className="hidden" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Contenu texte / Légende (PAS pour article_blog qui a son propre champ) */}
                {postType !== 'article_blog' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      onFocus={(e) => {
                        setIsInputFocused(true);
                        setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
                      }}
                      onBlur={() => setIsInputFocused(false)}
                      placeholder="Décris ton contenu..."
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                    />
                  </div>
                )}

                {/* Sélection du métier - OBLIGATOIRE (PAS pour article_blog qui a son propre) */}
                {postType !== 'article_blog' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Métier concerné <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <div 
                        className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1 pr-6"
                        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                      >
                        {METIERS_LIST.map((metier) => (
                          <button
                            key={metier.value}
                            onClick={() => setSelectedMetier(metier.value)}
                            className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                              selectedMetier === metier.value
                                ? 'bg-orange-500 text-white shadow-sm'
                                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                            }`}
                          >
                            {metier.label}
                          </button>
                        ))}
                      </div>
                      {/* Indicateur de scroll orange */}
                      <div className="absolute right-0 top-0 bottom-1 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none flex items-center justify-end pr-1">
                        <ChevronRight size={14} className="text-orange-500 animate-pulse" />
                      </div>
                    </div>
                    {!selectedMetier && (
                      <p className="text-xs text-red-500 mt-1">Choisis un métier pour ta publication</p>
                    )}
                  </div>
                )}

                {/* Localisation avec autocomplétion (PAS pour article_blog) */}
                {postType !== 'article_blog' && (
                <div className="relative" id="location-field-container">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ville ou code postal</label>
                  <div className="flex items-center gap-2 px-4 py-3 border border-gray-200 rounded-xl focus-within:ring-2 focus-within:ring-orange-500 focus-within:border-transparent">
                    <MapPin size={18} className="text-orange-500 flex-shrink-0" />
                    <input
                      ref={locationInputRef}
                      type="text"
                      inputMode="text"
                      autoComplete="off"
                      value={locationInput}
                      onChange={(e) => {
                        setLocationInput(e.target.value);
                        if (!e.target.value) {
                          setLocation('');
                          setLocationCoords(null);
                        }
                      }}
                      onFocus={(e) => {
                        setIsInputFocused(true);
                        if (locationSuggestions.length > 0) {
                          setShowLocationSuggestions(true);
                        }
                        // Scroll vers le champ quand le clavier s'ouvre sur mobile
                        setTimeout(() => {
                          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }, 300);
                      }}
                      onBlur={() => {
                        setIsInputFocused(false);
                        // Délai pour permettre le clic sur une suggestion
                        setTimeout(() => setShowLocationSuggestions(false), 200);
                      }}
                      placeholder="Ex: 34300 ou Agde..."
                      className="flex-1 bg-transparent border-0 outline-none text-sm"
                    />
                    {locationInput && (
                      <button
                        type="button"
                        onClick={() => {
                          setLocationInput('');
                          setLocation('');
                          setLocationCoords(null);
                          setLocationSuggestions([]);
                          locationInputRef.current?.focus();
                        }}
                        className="p-1 hover:bg-gray-100 rounded-full"
                      >
                        <X size={16} className="text-gray-400" />
                      </button>
                    )}
                  </div>
                  
                  {/* Suggestions dropdown */}
                  {showLocationSuggestions && locationSuggestions.length > 0 && (
                    <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                      {locationSuggestions.map((suggestion, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => handleSelectLocation(suggestion)}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-orange-50 transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <MapPin size={16} className="text-orange-500 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{suggestion.city}</p>
                            <p className="text-xs text-gray-500">{suggestion.postcode}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Indication de localisation sélectionnée */}
                  {location && locationCoords && (
                    <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                      <span>✓</span> Localisation enregistrée
                    </p>
                  )}
                </div>
                )}

                {/* Visibilité (PAS pour article_blog qui a son propre) */}
                {postType !== 'article_blog' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Visibilité</label>
                  <div className="flex gap-2">
                    {visibilityOptions.map((opt) => {
                      const Icon = opt.icon;
                      return (
                        <button
                          key={opt.id}
                          onClick={() => setVisibility(opt.id)}
                          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl text-sm font-medium transition-all ${
                            visibility === opt.id
                              ? 'bg-orange-500 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <Icon size={16} />
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                )}

                {/* Publier sur - Réseaux sociaux (PAS pour article_blog) */}
                {postType !== 'article_blog' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Publier sur</label>
                  <div className="space-y-2">
                    {/* AbracadaWorld - Toujours actif */}
                    <div className="flex items-center justify-between px-3 py-2.5 bg-orange-50 border border-orange-200 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                          <span className="text-white font-bold text-xs">A</span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">AbracadaWorld</span>
                      </div>
                      <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>

                    {/* Réseaux sociaux - Bientôt disponibles - Affichage en ligne scrollable */}
                    <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
                      {[
                        { name: 'Facebook', color: '#1877F2', icon: 'f' },
                        { name: 'Instagram', color: '#E4405F', icon: '📷' },
                        { name: 'TikTok', color: '#000000', icon: '♪' },
                        { name: 'LinkedIn', color: '#0A66C2', icon: 'in' },
                        { name: 'X', color: '#000000', icon: '𝕏' },
                      ].map((social) => (
                        <div 
                          key={social.name}
                          className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl opacity-50"
                        >
                          <div 
                            className="w-6 h-6 rounded-md flex items-center justify-center text-white text-[10px] font-bold"
                            style={{ backgroundColor: social.color }}
                          >
                            {social.icon}
                          </div>
                          <span className="text-xs font-medium text-gray-500">{social.name}</span>
                          <Lock size={12} className="text-gray-400" />
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-gray-400 text-center">
                      Publication multi-réseaux bientôt disponible
                    </p>
                  </div>
                </div>
                )}
              </div>
            )}
          </div>

          {/* Footer fixe - Caché quand le clavier est ouvert */}
          {step === 2 && !isInputFocused && keyboardHeight === 0 && (
            <div 
              className="flex-shrink-0 p-4 border-t border-gray-200 bg-white transition-all duration-200"
              style={{ 
                paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))'
              }}
            >
              {/* Bouton Créer avec Abra - Ouvre le chat Abra */}
              <button
                onClick={() => {
                  if (onAskAbra) {
                    // Ouvrir le chat Abra avec le contexte de la publication
                    onAskAbra({
                      type: postType,
                      title: title,
                      content: content,
                      metier: selectedMetier,
                    });
                    onClose();
                  } else {
                    toast.info('Connecte-toi pour utiliser l\'assistant Abra');
                  }
                }}
                data-testid="create-with-abra-btn"
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl transition-all text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200"
              >
                <Sparkles size={16} />
                Créer avec Abra
              </button>
              
              {/* Boutons Annuler et Poster */}
              <div className="flex gap-3">
                <button
                  onClick={handleClose}
                  data-testid="cancel-publication-btn"
                  className="flex-1 h-12 rounded-xl font-semibold transition-all border border-gray-300 text-[#1e3a5f] hover:bg-gray-100"
                >
                  Annuler
                </button>
                <button
                  onClick={() => handleSubmit(false)}
                  disabled={loading || !selectedMetier}
                  data-testid="submit-publication-btn"
                  className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                  Poster
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
